import { formatCurrency, formatNumber, formatPercent } from '../../../utils/formatUtils';
import {
  reportFieldIsCurrencyLike,
  reportFieldIsPercentLike,
  formatAggregatedDisplayForReport,
} from '../../../utils/reportCurrencyAggregate';
import { resolveReportCurrency } from '../matrix/matrixCurrencyFormat';

export function resolveChartMeasureField({
  chart,
  summarizedFields = {},
  summarizedData = {},
  selectedFields = [],
}) {
  const name =
    chart?.yAxisField ||
    chart?.yAxisFields?.[0] ||
    chart?.matrixMeasureField ||
    chart?.pieValueField ||
    chart?.funnelValueField;

  if (!name || name === 'record_count') {
    return {
      field_name: 'record_count',
      data_type: 'number',
      aggregation: 'count',
      is_virtual: true,
    };
  }

  const meta = summarizedData?._metadata?.summarized_fields?.[name];

  const fromSelected = (selectedFields || []).find(
    (f) =>
      f?.field_name === name ||
      String(f?.id) === String(name) ||
      String(f?.field_config_id) === String(name)
  );

  if (fromSelected) {
    const aggFromSummarized =
      summarizedFields[fromSelected.id]?.aggregation ||
      summarizedFields[fromSelected.field_config_id]?.aggregation;
    return {
      ...fromSelected,
      ...(meta || {}),
      field_name: fromSelected.field_name || name,
      aggregation:
        aggFromSummarized ||
        meta?.aggregation ||
        fromSelected.aggregation ||
        'sum',
    };
  }

  if (meta) {
    return {
      ...meta,
      field_name: meta.field_name || name,
      aggregation: meta.aggregation || 'sum',
    };
  }

  const summarizedKey = Object.keys(summarizedFields || {}).find((fieldId) => {
    const sf = selectedFields.find(
      (f) => String(f.id) === String(fieldId) || String(f.field_config_id) === String(fieldId)
    );
    return sf?.field_name === name;
  });

  if (summarizedKey) {
    const sf = selectedFields.find(
      (f) => String(f.id) === String(summarizedKey) || String(f.field_config_id) === String(summarizedKey)
    );
    if (sf) {
      return {
        ...sf,
        ...(meta || {}),
        aggregation: summarizedFields[summarizedKey]?.aggregation || meta?.aggregation || 'sum',
      };
    }
  }

  return { field_name: name, data_type: 'number', aggregation: 'sum' };
}

/**
 * Chart value formatters aligned with report table / matrix cells.
 */
export function buildChartCurrencyFormatters({
  measureField,
  baseCurrencyCode = null,
  multiCurrencyEnabled = true,
}) {
  const isCurrency = reportFieldIsCurrencyLike(measureField);
  const isPercent = reportFieldIsPercentLike(measureField);
  const tokenCurrency = resolveReportCurrency('INR');
  const displayCurrency =
    multiCurrencyEnabled && baseCurrencyCode
      ? baseCurrencyCode
      : isCurrency
        ? tokenCurrency
        : null;

  const decimalPlaces =
    measureField?.scale !== undefined &&
    measureField?.scale !== null &&
    measureField?.scale !== ''
      ? parseInt(measureField.scale, 10)
      : 2;

  const aggregation = (measureField?.aggregation || 'sum').toLowerCase();

  const formatChartValue = (value) => {
    if (value == null || value === '') return '';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (!Number.isFinite(num)) return String(value);

    if (measureField?.field_name === 'record_count' || aggregation === 'count') {
      return formatNumber(num, { maximumFractionDigits: 0 });
    }

    if (isPercent) {
      return formatPercent(num, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }

    if (isCurrency && displayCurrency) {
      return formatAggregatedDisplayForReport(
        num,
        measureField,
        aggregation,
        displayCurrency,
        (res) =>
          formatCurrency(res, displayCurrency, {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          })
      );
    }

    return formatNumber(num, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatChartAxisValue = (value) => {
    if (!Number.isFinite(Number(value))) return '';
    const num = Number(value);
    if (isPercent) {
      return formatPercent(num, { maximumFractionDigits: 0 });
    }

    if (!isCurrency || !displayCurrency) {
      return formatNumber(num, { maximumFractionDigits: 0 });
    }
    if (Math.abs(num) >= 1_000_000) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: displayCurrency,
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(num);
      } catch {
        return formatChartValue(num);
      }
    }
    return formatCurrency(num, displayCurrency, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return {
    isCurrency,
    isPercent,
    displayCurrency,
    formatChartValue,
    formatChartAxisValue,
  };
}

export function buildChartCurrencyFormattersFromContext(ctx) {
  const measureField = resolveChartMeasureField(ctx);
  return {
    measureField,
    ...buildChartCurrencyFormatters({
      measureField,
      baseCurrencyCode: ctx.baseCurrencyCode,
      multiCurrencyEnabled: ctx.multiCurrencyEnabled,
    }),
  };
}
